import { useEffect, useState, useRef, useCallback } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ContentProtection } from "./components/ContentProtection";
import { LanguageProvider } from "./context/LanguageContext";
import { api } from "./lib/api";
import { Navbar } from "./components/Navbar";
import { Footer } from "./components/Footer";
import { NewsletterAsk } from "./components/NewsletterAsk";
import { AuthModal } from "./components/AuthModal";
import { ScrollToTop } from "./components/ScrollToTop";
import { Toaster } from "./components/ui/sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./components/ui/dialog";
import { Button } from "./components/ui/button";
import { toast } from "sonner";
import { Loader2, Eye, Clock } from "lucide-react";

const IDLE_MS = 15 * 60 * 1000;   // 15 minutes total
const WARN_MS = 60 * 1000;        // warn 1 minute before logout

function InactivityGuard() {
  const { user, logout } = useAuth();
  const [warning, setWarning] = useState(false);
  const [count, setCount] = useState(60);
  const warnTimer = useRef(null);
  const logoutTimer = useRef(null);
  const countTimer = useRef(null);
  const warningRef = useRef(false);
  // Staff (admin/moderator) idle behaviour is owned by StaffChrome, which honours the
  // "Stay logged in" preference. Exclude them here so this hardcoded 15-min guard never
  // overrides that choice. It still applies to regular users.
  const isStaff = !!user && (user.role === "admin" || user.role === "moderator");
  const loggedIn = !!user && user !== false && !isStaff;

  useEffect(() => { warningRef.current = warning; }, [warning]);

  const clearAll = useCallback(() => {
    clearTimeout(warnTimer.current);
    clearTimeout(logoutTimer.current);
    clearInterval(countTimer.current);
  }, []);

  const doLogout = useCallback(async () => {
    clearAll();
    setWarning(false);
    try { await logout(); } catch { /* ignore */ }
    toast.message("You've been signed out due to inactivity", { description: "Please sign in again to continue." });
  }, [clearAll, logout]);

  const startTimers = useCallback(() => {
    clearAll();
    setWarning(false);
    warnTimer.current = setTimeout(() => {
      setWarning(true);
      setCount(60);
      countTimer.current = setInterval(() => setCount((c) => (c > 0 ? c - 1 : 0)), 1000);
    }, IDLE_MS - WARN_MS);
    logoutTimer.current = setTimeout(doLogout, IDLE_MS);
  }, [clearAll, doLogout]);

  useEffect(() => {
    if (!loggedIn) { clearAll(); setWarning(false); return; }
    startTimers();
    const onActivity = () => { if (!warningRef.current) startTimers(); };
    const events = ["mousemove", "mousedown", "keydown", "scroll", "touchstart"];
    events.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));
    return () => {
      events.forEach((e) => window.removeEventListener(e, onActivity));
      clearAll();
    };
  }, [loggedIn, startTimers, clearAll]);

  const stay = () => startTimers();

  if (!loggedIn || !warning) return null;
  return (
    <Dialog open={warning} onOpenChange={(o) => { if (!o) stay(); }}>
      <DialogContent className="sm:max-w-sm" data-testid="inactivity-dialog">
        <DialogHeader>
          <div className="flex items-center gap-2 mb-1">
            <span className="h-9 w-9 rounded-xl bg-amber-100 flex items-center justify-center"><Clock size={20} className="text-amber-600" /></span>
            <DialogTitle className="font-display text-xl text-[#12234A]">Still there?</DialogTitle>
          </div>
          <DialogDescription className="text-sm text-slate-500">
            You'll be signed out in <strong data-testid="inactivity-countdown">{count}</strong> second{count === 1 ? "" : "s"} to protect your account on shared devices.
          </DialogDescription>
        </DialogHeader>
        <div className="flex gap-2">
          <Button onClick={doLogout} variant="outline" data-testid="inactivity-logout" className="flex-1 rounded-xl">Sign out now</Button>
          <Button onClick={stay} data-testid="inactivity-stay" className="flex-1 rounded-xl bg-[#F5551E] hover:bg-[#E04812] text-white font-semibold">Stay signed in</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

import Home from "./pages/Home";
import Browse from "./pages/Browse";
import Litters from "./pages/Litters";
import LostFoundMap from "./pages/LostFoundMap";
import Businesses from "./pages/Businesses";
import Renew from "./pages/Renew";
import ListingDetail from "./pages/ListingDetail";
import CreateListing from "./pages/CreateListing";
import Dashboard from "./pages/Dashboard";
import SellerProfile from "./pages/SellerProfile";
import StaffLogin from "./pages/StaffLogin";
import AdminPanel from "./pages/AdminPanel";
import AdManager from "./pages/AdManager";
import ModeratorPanel from "./pages/ModeratorPanel";
import AuthCallback from "./pages/AuthCallback";
import SupportThread from "./pages/SupportThread";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import ConfirmEmail from "./pages/ConfirmEmail";
import Advertise from "./pages/Advertise";
import AdvertisingInfo from "./pages/AdvertisingInfo";
import PlacementsInfo from "./pages/PlacementsInfo";
import ContactUs from "./pages/ContactUs";
import Privacy from "./pages/Privacy";
import About from "./pages/About";
import HowItWorks from "./pages/HowItWorks";
import LitterJourney from "./pages/LitterJourney";
import SubscriptionPrefs from "./pages/SubscriptionPrefs";
import TrustSafety from "./pages/TrustSafety";
import GetVerified from "./pages/GetVerified";

function Protected({ children }) {
  const { user, setAuthOpen } = useAuth();
  useEffect(() => { if (user === false) setAuthOpen(true); }, [user, setAuthOpen]);
  if (user === null) return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="animate-spin text-[#F5551E]" size={30} /></div>;
  if (!user) return <Navigate to="/" replace />;
  return children;
}

const StaffLoader = () => <div className="min-h-screen bg-[#0E1B38] flex items-center justify-center"><Loader2 className="animate-spin text-[#F5551E]" size={30} /></div>;

function AdminOnly({ children }) {
  const { user } = useAuth();
  if (user === null) return <StaffLoader />;
  if (!user || user.role !== "admin") return <Navigate to="/staff" replace />;
  return children;
}

function StaffOnly({ children }) {
  const { user } = useAuth();
  if (user === null) return <StaffLoader />;
  if (!user || !["admin", "moderator"].includes(user.role)) return <Navigate to="/staff" replace />;
  return children;
}

function ImpersonationBanner() {
  const { user, stopImpersonation } = useAuth();
  const [exiting, setExiting] = useState(false);
  if (!user || !user.impersonating) return null;
  const exit = async () => {
    setExiting(true);
    try { await stopImpersonation(); window.location.href = "/admin"; }
    catch { setExiting(false); }
  };
  return (
    <div data-testid="impersonation-banner" className="sticky top-0 z-[60] bg-[#F5551E] text-white text-sm font-medium px-4 py-2 flex items-center justify-center gap-3">
      <span className="inline-flex items-center gap-1.5"><Eye size={15} /> Viewing as <strong>{user.name}</strong> (admin session)</span>
      <button onClick={exit} disabled={exiting} data-testid="exit-impersonation" className="underline underline-offset-2 hover:opacity-90">
        {exiting ? "Exiting…" : "Exit to admin"}
      </button>
    </div>
  );
}

function Layout() {
  return (
    <div className="min-h-screen flex flex-col">
      <ImpersonationBanner />
      <Navbar />
      <main className="flex-1">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/browse" element={<Browse />} />
          <Route path="/litters" element={<Litters />} />
          <Route path="/lost-found/map" element={<LostFoundMap />} />
          <Route path="/businesses" element={<Businesses key="business" />} />
          <Route path="/breeders" element={<Businesses key="breeder" kind="breeder" />} />
          <Route path="/renew/:listingId" element={<Renew />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/confirm-email" element={<ConfirmEmail />} />
          <Route path="/listing/:id" element={<ListingDetail />} />
          <Route path="/seller/:id" element={<SellerProfile />} />
          <Route path="/post" element={<Protected><CreateListing /></Protected>} />
          <Route path="/edit/:id" element={<Protected><CreateListing /></Protected>} />
          <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
          <Route path="/advertise" element={<Protected><Advertise /></Protected>} />
          <Route path="/advertising" element={<AdvertisingInfo />} />
          <Route path="/advertising/placements" element={<PlacementsInfo />} />
          <Route path="/contact" element={<ContactUs />} />
          <Route path="/about" element={<About />} />
          <Route path="/how-it-works" element={<HowItWorks />} />
          <Route path="/litter-journey" element={<LitterJourney />} />
          <Route path="/subscriptions" element={<SubscriptionPrefs />} />
          <Route path="/trust-safety" element={<TrustSafety />} />
          <Route path="/get-verified" element={<GetVerified />} />
          <Route path="/support/:token" element={<SupportThread />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      <Footer />
      <NewsletterAsk />
      <AuthModal />
    </div>
  );
}

function StaffArea() {
  return (
    <Routes>
      <Route path="/staff" element={<StaffLogin />} />
      <Route path="/admin" element={<AdminOnly><AdminPanel /></AdminOnly>} />
      <Route path="/admin/ads" element={<AdminOnly><AdManager /></AdminOnly>} />
      <Route path="/moderator" element={<StaffOnly><ModeratorPanel /></StaffOnly>} />
    </Routes>
  );
}

function usePresencePing() {
  useEffect(() => {
    let vid = localStorage.getItem("cp_visitor_id");
    if (!vid) {
      vid = (window.crypto?.randomUUID ? window.crypto.randomUUID() : Math.random().toString(36).slice(2) + Date.now().toString(36));
      localStorage.setItem("cp_visitor_id", vid);
    }
    const IDLE_MS = 60000; // idle after 60s without interaction
    let lastActivity = Date.now();
    let wasActive = true;
    const isActive = () => document.visibilityState === "visible" && (Date.now() - lastActivity < IDLE_MS);
    const ping = () => { api.post("/presence/ping", { visitor_id: vid, active: isActive() }).catch(() => {}); };
    const bump = () => {
      lastActivity = Date.now();
      if (!wasActive) { wasActive = true; ping(); } // resume from away -> ping immediately
    };
    ping();
    const t = setInterval(() => { wasActive = isActive(); ping(); }, 45000);
    const onVis = () => { if (document.visibilityState === "visible") { bump(); } else { wasActive = false; ping(); } };
    const acts = ["mousemove", "mousedown", "keydown", "scroll", "touchstart"];
    acts.forEach((e) => window.addEventListener(e, bump, { passive: true }));
    document.addEventListener("visibilitychange", onVis);
    return () => {
      clearInterval(t);
      document.removeEventListener("visibilitychange", onVis);
      acts.forEach((e) => window.removeEventListener(e, bump));
    };
  }, []);
}

function AppInner() {
  const location = useLocation();
  usePresencePing();
  useEffect(() => {
    if (!location.hash) window.scrollTo(0, 0);
  }, [location.pathname, location.hash]);
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  const p = location.pathname;
  const body = (p === "/staff" || p.startsWith("/admin") || p === "/moderator") ? <StaffArea /> : <Layout />;
  return (<><InactivityGuard />{body}<ScrollToTop /></>);
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <LanguageProvider>
          <AppInner />
          <ContentProtection />
          <Toaster position="top-center" richColors />
        </LanguageProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
